import { useColorModeValue } from '@chakra-ui/react';

/**
 * Custom hook that returns common color values based on the current color mode
 * This makes it easier to keep consistent styling across the application
 * @returns {Object} Object containing color values for the current color mode
 */
export const useThemeColors = () => {
  return {
    // Background colors
    cardBg: useColorModeValue('white', 'dark.card'),
    pageBg: useColorModeValue('light.bg', 'dark.bg'),
    statBg: useColorModeValue('blue.50', 'rgba(118, 120, 237, 0.1)'),
    chartBg: useColorModeValue('white', 'gray.800'),
    hoverBg: useColorModeValue('gray.50', 'rgba(255, 255, 255, 0.05)'),
    
    // Border colors
    borderColor: useColorModeValue('gray.200', 'dark.border'),
    
    // Text colors
    textColor: useColorModeValue('gray.800', 'dark.textPrimary'),
    secondaryTextColor: useColorModeValue('gray.600', 'dark.textSecondary'),
    
    // Status colors
    positiveColor: useColorModeValue('green.500', 'green.400'),
    negativeColor: useColorModeValue('red.500', 'red.400'),
    warningColor: useColorModeValue('yellow.500', 'yellow.400'),
    accentColor: useColorModeValue('brand.500', 'brand.400'),
    
    // Component-specific colors
    tableBorderColor: useColorModeValue('gray.200', 'dark.border'),
    inputBg: useColorModeValue('white', 'whiteAlpha.50'),
  };
};

/**
 * Returns chart colors suitable for the current theme
 * @returns {Object} Object with chart color values
 */
export const getChartColors = () => {
  // These aren't reactive since Chart.js can't handle reactive color changes well
  // For chart recoloring on theme change, you'll need to recreate the chart
  const isDark = document.documentElement.classList.contains('chakra-ui-dark');
  
  return {
    grid: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
    text: isDark ? '#a9a9c8' : '#4a5568',
    accent: '#7678ED',
    success: isDark ? '#00FF9D' : '#38A169',
    error: isDark ? '#FF0066' : '#E53E3E',
    warning: isDark ? '#FFCC00' : '#D69E2E',
    chartColors: [
      'rgba(118, 120, 237, 0.7)',
      'rgba(0, 255, 157, 0.7)',
      'rgba(255, 0, 102, 0.7)',
      'rgba(255, 204, 0, 0.7)',
      'rgba(90, 90, 255, 0.7)',
    ]
  };
};

/**
 * Returns chart options with proper theming based on the current color mode
 * @param {Object} customOptions - Additional chart options to merge
 * @returns {Object} Chart.js options object
 */
export const getThemedChartOptions = (customOptions = {}) => {
  const colors = getChartColors();
  
  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: colors.text,
          font: {
            family: '"Inter", sans-serif',
          }
        }
      },
      tooltip: {
        backgroundColor: colors.chartBg,
        titleColor: colors.text,
        bodyColor: colors.text,
        borderColor: colors.grid,
        borderWidth: 1,
      }
    },
    scales: {
      x: {
        grid: {
          color: colors.grid,
        },
        ticks: {
          color: colors.text,
          font: {
            family: '"Inter", sans-serif',
          }
        }
      },
      y: {
        grid: {
          color: colors.grid,
        },
        ticks: {
          color: colors.text,
          font: {
            family: '"Inter", sans-serif',
          }
        }
      }
    }
  };

  return { ...defaultOptions, ...customOptions };
}; 